import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import {
  Plus,
  Filter,
  Eye,
  Pencil,
  Trash2,
  MoreVertical,
  Database,
  Search,
  ShieldCheck,
  Download,
  Edit3,
  X,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import Pagination from "../../components/pagination";
import MainLayout from "../../layout";
import { url } from "../../config";
import FilterDrawer from "../../components/filter/filter-drawer";
import {
  TabelArrayText,
  TabelBooleanText,
  TabelImage,
} from "../../components/view-form-components/tabel-cell-view";
import { types, uiTypes } from "../../utils/types";
import DeleteModal from "../../components/modal/delete-modal";
import Loader from "../../assets/icons/loader/loader";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import moment from "moment";
import { createPortal } from "react-dom";
import ExportModal from "../../components/modal/export-modal";

const ListPage = ({ model }) => {
  const location = useLocation();
  const queryParamsData = new URLSearchParams(location.search);
  const page_no = parseInt(queryParamsData.get("page") ?? 1);

  const { tableName, columns, read, edit, create, references, icon } = model;
  const [data, setData] = useState({
    rows: [],
    page: 1,
    count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [deleteModalShow, setdeleteModalShow] = useState(false);
  const [selectDelete, setSelectDelete] = useState("");
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [exportModalShow, setExportModalShow] = useState(false);

  const dropdownRef = useRef(null);

  const columnHeadings = () => {
    var array = [];
    for (var i = 0; i < columns.length; i++) {
      var column = columns[i];
      if (column.ui?.list == null || column.ui.list == true) {
        array.push(column.name);
      }
    }
    return array;
  };

  const headings = columnHeadings();

  function normalizeParsedParams(parsed) {
    const result = {};
    for (const [key, value] of Object.entries(parsed)) {
      const match = key.match(/^(\w+)\[(\w+)\]$/);
      if (match) {
        const [, parent, child] = match;
        if (!result[parent]) result[parent] = {};
        result[parent][child] = value;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  const fetchData = () => {
    const rawFilters = queryString.parse(location.search, {
      arrayFormat: "bracket",
    });

    const filters = normalizeParsedParams(rawFilters);

    setLoading(true);
    axios
      .post(`${url}/model/${tableName}/list`, {
        page: page_no,
        ...filters,
      })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [tableName, page_no, location.search]);

  const searchParams = new URLSearchParams(location.search);
  searchParams.delete("page");
  const filters = searchParams.toString() ? `&${searchParams.toString()}` : "";

  const deleteFnc = (id) => {
    axios
      .post(`${url}/model/${tableName}/delete`, {
        id: id,
      })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
        fetchData();
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdownId]);

  const isSearchEnabled = columns.some((col) => col.ui?.search === true);

  const renderFormattedCell = (row, heading, column, onToggle) => {
    if (!column) return row[heading] ?? "--";

    if (column.isArray) return <TabelArrayText arrayData={row[heading]} />;
    if (column.type === types.BOOLEAN)
      return (
        <TabelBooleanText
          value={row[heading]}
          onChange={(newValue) => onToggle(row.id, heading, newValue)}
        />
      );
    if (column?.ui?.type === uiTypes.IMAGE)
      return <TabelImage url={row[heading]} />;

    const value = row[heading];
    const h = heading.toLowerCase();

    // Handle Status Column
    if (h === "status" || h === "enabled") {
      const isPositive = ["active", "enabled", "success", "paid"].includes(
        String(value).toLowerCase(),
      );
      const isNegative = ["inactive", "disabled", "expired", "failed"].includes(
        String(value).toLowerCase(),
      );

      if (column?.type === types.DATE) {
        return (
          <td className="px-4 py-3.5 max-w-xs">
            <span className="block truncate text-gray-900 font-normal">
              {row[heading]
                ? moment(row[heading]).format("DD-MM-YYYY hh:MM A")
                : "--"}
            </span>
          </td>
        );
      }

      if (column?.type === types.DATEONLY) {
        return (
          <td className="px-4 py-3.5 max-w-xs">
            <span className="block truncate text-gray-900 font-normal">
              {row[heading] ? moment(row[heading]).format("DD-MM-YYYY") : "--"}
            </span>
          </td>
        );
      }

      return (
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
            isPositive
              ? "bg-emerald-50 text-emerald-600 border-emerald-100"
              : isNegative
                ? "bg-red-50 text-red-500 border-red-100"
                : "bg-slate-100 text-slate-500 border-slate-200"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${isPositive ? "bg-emerald-500" : isNegative ? "bg-red-500" : "bg-slate-400"}`}
          ></span>
          {value}
        </span>
      );
    }

    // Handle Currency
    if (h.includes("price") || h.includes("amount") || h.includes("total")) {
      return (
        <span className="text-slate-900 font-bold tabular-nums">
          ₹{" "}
          {parseFloat(value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
          })}
        </span>
      );
    }

    return (
      <span className="truncate block max-w-[200px]" title={value}>
        {value ?? "--"}
      </span>
    );
  };

  const handleToggleBoolean = (id, fieldName, newValue) => {
    const currentRow = data.rows.find((r) => r.id === id);

    if (!currentRow) return;

    const updatedValues = {
      ...currentRow,
      [fieldName]: newValue,
    };

    axios
      .post(`${url}/model/${tableName}/edit`, {
        where: { id: id },
        values: updatedValues,
      })
      .then(() => {
        fetchData();
      })
      .catch((error) => {
        console.error("Update Error:", error);
      });
  };

  const renderIcon = (iconProp, pathSegment) => {
    let IconComponent;
    if (typeof iconProp === "string") {
      IconComponent = LucideIcons[iconProp] || LucideIcons.Database;
    } else if (iconProp) {
      IconComponent = iconProp;
    } else {
      IconComponent = LucideIcons.Database;
    }

    return <IconComponent />;
  };

  const renderBgIcon = (iconProp, pathSegment) => {
    let IconComponent;
    if (typeof iconProp === "string") {
      IconComponent = LucideIcons[iconProp] || LucideIcons.Database;
    } else if (iconProp) {
      IconComponent = iconProp;
    } else {
      IconComponent = LucideIcons.Database;
    }

    return (
      <IconComponent
        size={60}
        className="absolute left-[65%] top-0 text-slate-200/30 pointer-events-none z-0"
      />
    );
  };

  return (
    <MainLayout>
      <DeleteModal
        isOpen={deleteModalShow}
        onClose={() => {
          setdeleteModalShow(false);
          setSelectDelete("");
        }}
        btnFnc={() => deleteFnc(selectDelete)}
      />

      <ExportModal
        isOpen={exportModalShow}
        onClose={() => {
          setExportModalShow(false);
        }}
        tableName={tableName}
        columns={columns}
      />

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        columns={columns}
        references={references}
        queryParamsData={queryParamsData}
      />

      <div className="flex flex-col h-full bg-[#F8FAFC] relative overflow-hidden rounded-b-2xl">
        {/* --- Header --- */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 py-3.5 sm:py-4 transition-all">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4  mx-auto">
            {/* Title Section */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2.5 bg-primary rounded-xl text-white shadow-md shadow-blue-500/20 shrink-0">
                {renderIcon(icon)}
              </div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-slate-800 capitalize truncate leading-tight">
                  {tableName ? formatUnderscoreToSpace(tableName) : "Directory"}
                </h1>
                <p className="text-xs text-slate-500 font-medium truncate hidden min-[380px]:block">
                  Manage and view your{" "}
                  {tableName ? formatUnderscoreToSpace(tableName) : ""}{" "}
                  directory
                </p>
              </div>
            </div>{" "}
            {renderBgIcon(icon)}
            {/* Action Controls Section */}
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              {/* Search Input */}
              {isSearchEnabled && (
                <div className="relative flex-1 min-w-[140px] sm:min-w-[200px] md:w-56 lg:w-64">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder={`Search ${tableName ? formatUnderscoreToSpace(tableName) : ""}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 bg-slate-50 sm:bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      type="button"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}

              {/* Action Buttons Container */}
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                {/* Export Button */}
                <button
                  type="button"
                  onClick={() => setExportModalShow(true)}
                  title="Export"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900 shadow-sm transition-all active:scale-[0.98]"
                >
                  <Download size={16} className="text-slate-500" />
                  <span className="hidden sm:inline">Export</span>
                </button>

                {/* Filters Button */}
                <button
                  type="button"
                  onClick={() => setFilterDrawerOpen(true)}
                  title="Filters"
                  className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-all shadow-sm active:scale-[0.98]"
                >
                  <Filter size={16} className="text-slate-500" />
                  <span className="hidden sm:inline">Filters</span>
                </button>

                {/* Create Button */}
                <Link
                  to={`/${tableName}/create`}
                  title="Create Record"
                  className="flex items-center justify-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-blue-600 transition-all shadow-md shadow-blue-500/20 active:scale-[0.98] shrink-0"
                >
                  <Plus size={18} strokeWidth={2.5} />
                  <span>
                    Create
                    <span className="hidden min-[480px]:inline ml-1 capitalize">
                      {tableName ? formatUnderscoreToSpace(tableName) : ""}
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* --- Table Section --- */}
        <main className="flex-1 overflow-auto p-2 z-10">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col justify-between">
            <div className="overflow-x-auto custom-scrollbar w-full max-w-[83.5vw]">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                  <tr className="border-b border-slate-100 text-nowrap">
                    <th className="sticky top-0 bg-slate-50 px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                      #
                    </th>
                    {headings.map((heading) => (
                      <th
                        key={heading}
                        className="sticky top-0 z-20 bg-slate-50 px-3 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest"
                      >
                        {formatUnderscoreToSpace(heading)}
                      </th>
                    ))}
                    <th className="px-4 sm:px-6 py-4 text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right sticky right-0 z-50 bg-slate-50 border-b border-slate-100 shadow-[-1px_0_0_0_#f1f5f9]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-nowrap">
                  {loading ? (
                    <tr>
                      <td colSpan={headings.length + 2} className="py-24">
                        <div className="flex flex-col items-center justify-center">
                          <div className="h-40 w-40">
                            <Loader />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : data.rows?.length > 0 ? (
                    data.rows.map((row, index) => (
                      <tr
                        key={row.id || index}
                        className="group hover:bg-slate-50/80 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-slate-400 font-medium">
                          {(data.page - 1) * 10 + (index + 1)}
                        </td>
                        {headings.map((heading, hIdx) => {
                          const column = columns.find(
                            (val) => val.name === heading,
                          );

                          if (column?.isArray) {
                            return (
                              <td
                                key={hIdx}
                                className="px-3 py-4 whitespace-nowrap"
                              >
                                <TabelArrayText arrayData={row[heading]} />
                              </td>
                            );
                          }
                          if (column?.type === types.BOOLEAN) {
                            return (
                              <td
                                key={hIdx}
                                className="px-3 py-4 whitespace-nowrap"
                              >
                                <TabelBooleanText value={row[heading]} />
                              </td>
                            );
                          }
                          if (
                            column?.ui?.type === uiTypes.IMAGE ||
                            column?.ui?.type === uiTypes.DOCUMENT
                          ) {
                            return (
                              <td
                                key={hIdx}
                                className="px-3 py-4 whitespace-nowrap"
                              >
                                <TabelImage url={row[heading]} />
                              </td>
                            );
                          }
                          if (column?.type === types.DATE) {
                            return (
                              <td key={hIdx} className="px-3 py-4 max-w-xs">
                                <span className="text-sm font-medium text-slate-700 group-hover:text-primary transition-colors">
                                  {row[heading]
                                    ? moment(row[heading]).format(
                                        "DD-MM-YYYY hh:MM A",
                                      )
                                    : "--"}
                                </span>
                              </td>
                            );
                          }

                          if (column?.type === types.DATEONLY) {
                            return (
                              <td key={hIdx} className="px-3 py-4 max-w-xs">
                                <span className="text-sm font-medium text-slate-700 group-hover:text-primary transition-colors">
                                  {row[heading]
                                    ? moment(row[heading]).format("DD-MM-YYYY")
                                    : "--"}
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td key={hIdx} className="px-3 py-4 max-w-xs">
                              <p
                                title={row[heading]}
                                className="text-sm font-medium m-0 p-0 text-slate-700 group-hover:text-primary transition-colors text-ellipsis truncate"
                              >
                                {row[heading] ?? "--"}
                              </p>
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 bg-white/95 px-3 py-4 text-right backdrop-blur-sm group-hover:bg-gray-50/95">
                          <div className="hidden items-center justify-end gap-1 md:flex">
                            <Link
                              to={`/${tableName}/view/${row["id"]}`}
                              className="flex items-center gap-2 p-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              onClick={() => setOpenDropdownId(null)}
                            >
                              <Eye className="h-3.5 w-3.5 text-gray-400" /> View
                            </Link>
                            <Link
                              to={`/${tableName}/edit/${row["id"]}`}
                              className="flex items-center gap-2 p-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                              onClick={() => setOpenDropdownId(null)}
                            >
                              <Pencil className="h-3.5 w-3.5 text-indigo-500" />{" "}
                              Edit
                            </Link>
                            <button
                              onClick={() => {
                                setdeleteModalShow(true);
                                setSelectDelete(row["id"]);
                                setOpenDropdownId(null);
                              }}
                              className="flex  items-center gap-2 p-2 text-xs font-medium text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />{" "}
                              Delete
                            </button>
                          </div>

                          <ActionDropdown
                            row={row}
                            tableName={tableName}
                            setdeleteModalShow={setdeleteModalShow}
                            setSelectDelete={setSelectDelete}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={headings.length + 2}
                        className="py-20 text-center"
                      >
                        <div className="flex flex-col items-center text-slate-300">
                          <Database size={48} className="mb-2 opacity-20" />
                          <p className="text-sm font-medium">
                            No records found for this model
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* --- Simple Pagination Placeholder --- */}
            <div className="px-6 py-2 bg-slate-50/30 border-t border-slate-100 flex justify-end items-center">
              <div className="px-2 sm:px-4 bg-white flex justify-center sm:justify-end items-center">
                <div
                  className={`w-full ${loading ? "pointer-events-none opacity-45" : ""}`}
                >
                  <Pagination
                    totalPages={parseInt(data?.count) ?? 0}
                    pagination={parseInt(data?.page) ?? 0}
                    path={`/${tableName}/list`}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </MainLayout>
  );
};

export default ListPage;

const ActionDropdown = ({
  row,
  tableName,
  setdeleteModalShow,
  setSelectDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 128, // 128px is width of menu (w-32)
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) setIsOpen(false); // Close menu on scroll to prevent floating offset
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  return (
    <div className="inline-block text-left md:hidden">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
            className="fixed z-[9999] w-32 rounded-lg border border-gray-100 bg-white py-1 shadow-lg"
          >
            <Link
              to={`/${tableName}/view/${row["id"]}`}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <Eye className="h-3.5 w-3.5 text-gray-400" /> View
            </Link>
            <Link
              to={`/${tableName}/edit/${row["id"]}`}
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              <Pencil className="h-3.5 w-3.5 text-indigo-500" /> Edit
            </Link>
            <button
              onClick={() => {
                setdeleteModalShow(true);
                setSelectDelete(row["id"]);
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" /> Delete
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};
